import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Calendar,
  BookOpen,
  AlertCircle,
  Play,
  Lock,
  CheckCircle,
  Users,
  Brain,
  Trophy,
  Award,
  Target,
  UserCheck,
  X,
  User,
  Mail,
  Phone,
  BookOpenText,
  CheckCircle2,
  PlayCircle,
  RefreshCw,
  Search,
} from "lucide-react";
import { getMyQuizzes } from "../../../../services/MyQuizzesService";
import { toast } from '../../common/Toast/toast';
import { Skeleton, SkeletonGroup } from '../../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../../common/Loader/busy';
import "../../Trainer/TrainerDashboard.css";
import "../../Dashboard/BusinessPulse.css";
import { useAuth } from "../../../../context/AuthContext";
import { getStudentOptions } from "../../../../services/StudentService";
import CustomAsyncDropdown from "../../common/NewCustomAsyncSelectDropdown";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import useScrollLoadMore from "../../common/useScrollLoadMore";
import debounce from "lodash/debounce";
import FilterSheet from "../../common/FilterSheet/FilterSheet";

const StudentQuizList = () => {
  const [quizHistories, setQuizHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedStudentValue, setSelectedStudentValue] = useState(null);
  
  // Pagination & infinite scroll state for admin dropdown
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);

  /* The record the row-click popup is showing, or null. */
  const [openQuiz, setOpenQuiz] = useState(null);

  /* Infinite scroll, the same shape ListAttendance uses — no pager
     buttons, an observer on the foot of the list as the page scrolls,
     and a count under the list saying how far down it the reader is.

     The difference: getMyQuizzes answers with every row at once, so
     there is no next page to fetch. This reveals more of what is
     already in hand instead. */
  const SCROLL_STEP = 20;
  const [shownCount, setShownCount] = useState(SCROLL_STEP);
  const listRef = useRef(null);
  // The foot under the list -- the sentinel, in the flow of the page.
  const listEndRef = useRef(null);

  useEffect(() => {
    setShownCount(SCROLL_STEP);
  }, [quizHistories]);

  // The page is the scroller: the foot of the list reaching the viewport
  // is what asks for the next slice. See useScrollLoadMore.
  useScrollLoadMore(
    listEndRef,
    () => setShownCount((n) => (n >= quizHistories.length ? n : n + SCROLL_STEP)),
    [quizHistories.length, shownCount],
  );


  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const isFetching = useRef(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch students from API with pagination and search
  const fetchStudents = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetching.current = true;
      // Names only (server skips the join): the picker reads id, name, email.
      const response = await getStudentOptions(20, pageNum, search);
      const studentsList = response.data || [];
      const hasMore = studentsList.length === 20;
      return { students: studentsList, hasMore };
    } catch (err) {
      console.error("Error fetching students:", err);
      return { students: [], hasMore: false };
    } finally {
      isFetching.current = false;
    }
  }, []);

  // Load students for dropdown
  const loadStudents = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      setLoadingStudents(true);
      const { students: studentsList, hasMore } = await fetchStudents(search, pageNum);

      const newOptions = studentsList.map((s) => ({
        value: s.id,
        label: `${s.name} | ${s.email}`,
        student: s,
      }));

      if (append) {
        setStudentOptions((prev) => [...prev, ...newOptions]);
      } else {
        setStudentOptions(newOptions);
      }

      setHasMoreStudents(hasMore);
      setLoadingStudents(false);

      return { students: studentsList, hasMore };
    },
    [fetchStudents],
  );

  // Load more students for infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (!hasMoreStudents || loadingStudents || isFetching.current) return;

    const nextPage = studentPage + 1;
    setStudentPage(nextPage);
    await loadStudents(studentSearchTerm, nextPage, true);
  }, [
    hasMoreStudents,
    loadingStudents,
    studentPage,
    studentSearchTerm,
    loadStudents,
  ]);

  // Debounced search handler for admin dropdown
  const handleStudentSearch = useMemo(
    () =>
      debounce(async (inputValue) => {
        setStudentSearchTerm(inputValue);
        setStudentPage(1);
        await loadStudents(inputValue, 1, false);
      }, 700),
    [loadStudents],
  );

  // Handle refresh button click
  const handleRefresh = useCallback(async () => {
    if (!user?.isSuperAdmin) return;
    
    // Reset search term and load fresh data
    setStudentSearchTerm("");
    setStudentPage(1);
    await loadStudents("", 1, false);
  }, [user?.isSuperAdmin, loadStudents]);

  // Handle dropdown open - load students only when dropdown is opened
  const handleDropdownOpen = useCallback(async () => {
    // Only load if we haven't loaded any students yet
    if (user?.isSuperAdmin && studentOptions.length === 0 && !loadingStudents) {
      await loadStudents("", 1, false);
    }
  }, [user?.isSuperAdmin, studentOptions.length, loadingStudents, loadStudents]);

  const fetchStudentQuizHistories = async (studentId = null) => {
    try {
      setError(null);
      
      // For admin users, if no student is selected, don't load any data
      if (user?.isSuperAdmin && !studentId) {
        setQuizHistories([]);
        setLoading(false);
        return;
      }

      // Show loading states appropriately
      if (user?.isSuperAdmin && studentId) {
        // Both states draw a skeleton, so the global overlay stays down.
        silenceFor();
        setCardsLoading(true);
      } else if (!user?.isSuperAdmin) {
        silenceFor();
        setLoading(true);
      }

      let response;

      // Use the getMyQuizzes API only - no other API calls
      if (user?.isSuperAdmin && studentId) {
        // For admin viewing a specific student, find the student's email from options
        const selectedStudentObj = studentOptions.find(s => s.value === studentId)?.student;
        const studentEmail = selectedStudentObj?.email;
        if (studentEmail) {
          response = await getMyQuizzes(studentEmail);
        } else {
          setQuizHistories([]);
          setLoading(false);
          return;
        }
      } else if (!user?.isSuperAdmin) {
        // For regular student, no email needed (uses logged-in user's ID from token)
        response = await getMyQuizzes();
      } else {
        // Admin with no student selected
        setQuizHistories([]);
        setLoading(false);
        return;
      }

      // Check if API response has error
      if (response?.isError === true) {
        // API returned an error, but we might still have data
        const quizData = response?.data?.quizzes || [];
        
        if (quizData.length === 0) {
          // No data available, show appropriate message based on error
          if (response?.message?.code === "QUE403") {
            // This is a "no quizzes" error, not a critical error
            setQuizHistories([]);
            setError(null);
          } else {
            // Other errors
            setError(response?.message?.message || "Failed to load quiz data");
            setQuizHistories([]);
          }
        } else {
          // We have data despite the error flag
          const transformedQuizzes = transformQuizData(quizData);
          setQuizHistories(transformedQuizzes);
          setError(null);
        }
      } else {
        // Success response
        const quizData = response?.data?.quizzes || [];
        
        if (quizData.length === 0) {
          setQuizHistories([]);
          setError(null);
        } else {
          const transformedQuizzes = transformQuizData(quizData);
          setQuizHistories(transformedQuizzes);
          setError(null);
        }
      }

    } catch (err) {
      console.error("Error fetching student quiz histories:", err);
      setError("Failed to load quiz histories. Please try again later.");
      toast.error("Failed to load quiz histories");
      setQuizHistories([]);
    } finally {
      setLoading(false);
      setCardsLoading(false);
    }
  };

  // Helper function to transform quiz data
  const transformQuizData = (quizData) => {
    return quizData.map(quiz => {
      // Determine status based on isAttended and dates
      let status = "available";
      const now = new Date();
      const startDate = new Date(quiz.startDate);
      const endDate = new Date(quiz.endDate);
      
      if (quiz.isAttended) {
        status = "submitted";
      } else if (now < startDate) {
        status = "upcoming";
      } else if (now > endDate) {
        status = "deadline_passed";
      } else {
        status = "available";
      }

      // Get course name from the courseId array
      const courseName = quiz.courseId?.[0]?.name || "N/A";
      const courseId = quiz.courseId?.[0]?.id || null;

      // Get quiz title from quizId array
      const quizTitle = quiz.quizId?.[0]?.title || "Quiz";
      const quizIdValue = quiz.quizId?.[0]?.id || quiz.quizId;

      // Get batch names from batchIds array
      const batchNames = quiz.batchIds?.map(batch => batch.name).join(", ") || "N/A";

      // Get result details if available
      const result = quiz.result;
      const isAttended = quiz.isAttended;
      const hasResult = result !== null && result !== undefined;

      return {
        id: quiz.id,
        quizId: quizIdValue,
        quizName: quizTitle,
        courseId: courseId,
        courseName: courseName,
        batchNames: batchNames,
        startDate: quiz.startDate,
        endDate: quiz.endDate,
        status: status,
        isSubmitted: isAttended,
        isAttended: isAttended,
        canTakeQuiz: !isAttended && status === "available",
        actionLabel: isAttended ? "View Results" : 
                     status === "available" ? "Take Quiz" : 
                     status === "upcoming" ? "Upcoming" : "Expired",
        submission: isAttended && hasResult ? {
          score: `${result.scored}/${result.outOf}`,
          result: result.status,
          timeConsumed: result.timeConsumed,
          percentage: result.percentage
        } : null
      };
    });
  };

  useEffect(() => {
    if (user?.isSuperAdmin) {
      // Load initial students for dropdown (lazy load - only when dropdown opens)
      // Don't load quiz data for admin until student is selected
      setLoading(false);
    } else {
      fetchStudentQuizHistories();
    }
  }, [user]);

  // Admin: Handle student selection
  const handleStudentSelection = async (selectedOptions) => {
    const selectedOption = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;

    if (!selectedOption) {
      setSelectedStudent("");
      setSelectedStudentValue(null);
      setQuizHistories([]);
      setError(null);
      // Clear search term when selection is cleared
      setStudentSearchTerm("");
      return;
    }

    const { value: studentId, label: fullLabel, student } = selectedOption;
    setSelectedStudent(studentId);
    setSelectedStudentValue(selectedOption);
    await fetchStudentQuizHistories(studentId);
  };

  const handleClearSelection = () => {
    setSelectedStudent("");
    setSelectedStudentValue(null);
    setQuizHistories([]);
    setError(null);
    // Clear search term when clearing selection
    setStudentSearchTerm("");
  };

  const getQuizStatus = (quizHistory) => {
    const { status } = quizHistory;

    switch (status) {
      case "submitted":
        return {
          status: "submitted",
          label: "Completed",
          color: "success",
          icon: CheckCircle,
          variant: "success",
        };
      case "available":
        return {
          status: "available",
          label: "Available",
          color: "success",
          icon: Play,
          variant: "success",
        };
      case "upcoming":
        return {
          status: "upcoming",
          label: "Upcoming",
          color: "warning",
          icon: Clock,
          variant: "warning",
        };
      case "deadline_passed":
        return {
          status: "deadline_passed",
          label: "Deadline Passed",
          color: "danger",
          icon: Lock,
          variant: "danger",
        };
      default:
        return {
          status: "unknown",
          label: "Unknown",
          color: "secondary",
          icon: AlertCircle,
          variant: "secondary",
        };
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;

    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  /* The results dialog used to be a SweetAlert built from an HTML
     string, with confirmButtonColor "#ea580c" and a malformed
     `<spanPass"` fragment in its body. It is a real element now,
     rendered from this state and styled with the page's own rules. */

  const stats = useMemo(() => {
    if (user?.isSuperAdmin && !selectedStudent) {
      return { total: 0, available: 0, completed: 0, upcoming: 0, passed: 0 };
    }

    const total = quizHistories.length;
    const available = quizHistories.filter(
      (q) => q.status === "available"
    ).length;
    const completed = quizHistories.filter(
      (q) => q.status === "submitted"
    ).length;
    const upcoming = quizHistories.filter(
      (q) => q.status === "upcoming"
    ).length;
    const passed = quizHistories.filter(
      (q) => q.submission?.result === "Pass"
    ).length;

    return {
      total,
      available,
      completed,
      upcoming,
      passed,
    };
  }, [quizHistories, user?.isSuperAdmin, selectedStudent]);

  if (loading && !user?.isSuperAdmin) {
    /* The page's own shape while the quizzes load: the four figure tiles
       and the list's rows. The fetch silences the global overlay, so this
       is what is seen. */
    return (
      <div className="dashboard-wrapper">
        <div className="td-page">
          <SkeletonGroup label="Loading your quizzes" className="lms-skel-stack">
            <div className="ov-tiles">
              {[0, 1, 2, 3].map((i) => (
                <div className="ov-tile" key={i}>
                  <Skeleton width="55" />
                  <Skeleton shape="title" width="25" />
                </div>
              ))}
            </div>
            <div className="lms-skel-stack">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton shape="row" key={i} />
              ))}
            </div>
          </SkeletonGroup>
        </div>
      </div>
    );
  }

  const getSelectedStudentDetails = () => {
    return studentOptions.find(opt => opt.value === selectedStudent)?.student;
  };

  const selectedStudentDetails = getSelectedStudentDetails();

  /* Status, as a class rather than the inline colours this component
     used to carry. `available` was #F26A1B orange; it is the house blue
     now, like every other action on these pages. */
  /* Status, as a class rather than the inline colours this component
     used to carry. `available` was #F26A1B orange; it is the house blue
     now, like every other action on these pages. */
  /* Status, as one of the shared pill tones. `available` used to be
     inline #F26A1B orange; live/good/warn/bad are the pills the rest
     of this project already uses. */
  const STATUS_MAP = {
    submitted:       { label: "Completed",       pill: "pg-pill pg-pill--good" },
    available:       { label: "Available",       pill: "pg-pill pg-pill--live" },
    upcoming:        { label: "Upcoming",        pill: "pg-pill pg-pill--warn" },
    deadline_passed: { label: "Deadline passed", pill: "pg-pill" },
  };

  const isStaff = Boolean(user?.isSuperAdmin);
  const showList = !isStaff || Boolean(selectedStudent);

  /* One dropdown, rendered into the gate before a student is picked and
     into the strip afterwards -- the same shape /student-profile and
     /student-dashboard use. Declared here, below every handler and list
     it reads, so nothing is referenced before it is initialised. */
  const studentPicker = (
    <CustomAsyncDropdown
      inputId="sql-student-picker"
      options={studentOptions}
      value={selectedStudentValue ? [selectedStudentValue] : []}
      onChange={handleStudentSelection}
      placeholder="Search by name, email or phone…"
      isMulti={false}
      isSearchable={true}
      isLoading={loadingStudents}
      onInputChange={handleStudentSearch}
      onMenuScrollToBottom={handleLoadMore}
      onMenuOpen={handleDropdownOpen}
    />
  );

  const startQuiz = (q) => {
    sessionStorage.setItem("currentQuizId", q.quizId);
    sessionStorage.setItem("currentQuizTitle", q.quizName);
    sessionStorage.setItem("currentQuizHistoryId", q.id);
    if (isStaff && selectedStudent) {
      sessionStorage.setItem("adminViewingStudentId", selectedStudent);
      sessionStorage.setItem("currentStudentName", selectedStudentDetails?.name || "");
    }
    navigate("/student-quiz");
  };

  const fmt = (d) => (d ? formatDateTime(d) : "—");

  /* Columns, in the shape NewCustomDataTable takes. The row itself is
     the control — clicking anywhere on it opens the record — so no
     cell carries its own button. */
  const headers = [
    {
      title: "Quiz",
      prop: "quizName",
      searchable: true,
      sortable: true,
      cell: (row) => <span className="pg-strong">{row.quizName || "—"}</span>,
    },
    {
      title: "Course",
      prop: "courseName",
      searchable: true,
      sortable: true,
      cell: (row) => <span className="pg-course">{row.courseName || "—"}</span>,
    },
    {
      /* Opens and closes were two columns; the window is one fact and
         the list is capped at five, so they share a cell. Both dates in
         full are in the popup. */
      title: "Window",
      prop: "startDate",
      sortable: true,
      cell: (row) => (
        <span className="pg-date">
          {fmt(row.startDate)} – {fmt(row.endDate)}
        </span>
      ),
    },
    {
      title: "Status",
      prop: "status",
      sortable: true,
      cell: (row) => {
        const s = STATUS_MAP[row.status] || STATUS_MAP.deadline_passed;
        return <span className={s.pill}>{s.label}</span>;
      },
    },
    {
      title: "Score",
      prop: "score",
      sortable: false,
      cell: (row) =>
        row.isSubmitted && row.submission ? (
          <span className="pg-strong">
            {row.submission.percentage != null
              ? `${row.submission.percentage}%`
              : row.submission.score}
          </span>
        ) : (
          <span className="pg-none">—</span>
        ),
    },
  ];

  /* ── The record popup ──────────────────────────────────────────
     Opened by clicking a row, the way the attendance list opens one.
     ---------------------------------------------------------------- */
  const detail = openQuiz
    ? (() => {
        const s = STATUS_MAP[openQuiz.status] || STATUS_MAP.deadline_passed;
        const sub = openQuiz.submission;
        const passed = String(sub?.result || "").toLowerCase().includes("pass");

        return (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sq-detail-title"
            onClick={() => setOpenQuiz(null)}
          >
            <div
              className="pg-modal-panel pg-modal-panel--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="pgm-head">
                <div className="pgm-head-main">
                  <h2 className="pgm-batch" id="sq-detail-title">
                    {openQuiz.quizName}
                  </h2>
                  <p className="pgm-sub">
                    {openQuiz.courseName}
                    {openQuiz.batchNames ? ` · ${openQuiz.batchNames}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setOpenQuiz(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                {sub ? (
                  <div className="pgm-tiles">
                    <div className={`pgm-tile${passed ? " pgm-tile--good" : " pgm-tile--bad"}`}>
                      <span>Score</span>
                      <b>{sub.score ?? "—"}</b>
                    </div>
                    <div className="pgm-tile">
                      <span>Percentage</span>
                      <b>
                        {sub.percentage != null ? `${sub.percentage}%` : "—"}
                      </b>
                    </div>
                    <div className="pgm-tile">
                      <span>Time taken</span>
                      <b>{sub.timeConsumed || "—"}</b>
                    </div>
                  </div>
                ) : (
                  <p className="pgm-empty">
                    This quiz has not been attempted yet, so there is no score to show.
                  </p>
                )}

                <div className="pgm-section">
                  <h3 className="pgm-section-title">Details</h3>
                  <div className="pgm-facts">
                    <div className="pgm-fact">
                      <span className="pg-label">Status</span>
                      <span className={s.pill}>{s.label}</span>
                    </div>
                    <div className="pgm-fact">
                      <span className="pg-label">Result</span>
                      {sub?.result ? (
                        <span className={`pgm-pill${passed ? " pgm-pill--good" : " pgm-pill--bad"}`}>
                          {sub.result}
                        </span>
                      ) : (
                        <span className="pg-none">Not attempted</span>
                      )}
                    </div>
                    <div className="pgm-fact">
                      <span className="pg-label">Opens</span>
                      <span className="pgm-strong">{fmt(openQuiz.startDate)}</span>
                    </div>
                    <div className="pgm-fact">
                      <span className="pg-label">Closes</span>
                      <span className="pgm-strong">{fmt(openQuiz.endDate)}</span>
                    </div>
                    <div className="pgm-fact">
                      <span className="pg-label">Course</span>
                      <span className="pgm-strong">{openQuiz.courseName || "—"}</span>
                    </div>
                    <div className="pgm-fact">
                      <span className="pg-label">Batch</span>
                      <span className="pgm-strong">{openQuiz.batchNames || "—"}</span>
                    </div>
                  </div>
                </div>
              </div>

              <footer className="pg-modal-foot">
                <button
                  type="button"
                  className="pgm-btn"
                  onClick={() => setOpenQuiz(null)}
                >
                  Close
                </button>
                {openQuiz.canTakeQuiz && (
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    onClick={() => startQuiz(openQuiz)}
                  >
                    Take quiz
                  </button>
                )}
              </footer>
            </div>
          </div>
        );
      })()
    : null;

  return (
    <div className="dashboard-wrapper">
      <div className="td-page">
        {/* Staff pick whose quizzes they are reading. Before anything is
            chosen the picker sits inside the gate, the way every other
            "pick a student" page does it -- the page has nothing else to
            show yet, so a filter strip at the top with a "nothing
            selected" notice far below it pointing back up was two pieces
            of one idea. Once a student is picked it becomes the compact
            strip below. Same control either way. */}
        {isStaff && !selectedStudent && (
          <div className="pg-gate">
            <span className="pg-gate-mark">
              <Users size={22} aria-hidden="true" />
            </span>
            <h1 className="pg-gate-title">Pick a student</h1>
            <p className="pg-gate-text">
              Choose a student to see the quizzes assigned to them.
            </p>

            <div className="pg-gate-field">{studentPicker}</div>

            <span className="pg-gate-tip">
              <AlertCircle size={14} aria-hidden="true" />
              Search by name, email or phone number.
            </span>
          </div>
        )}

        {isStaff && selectedStudent && (
          <FilterSheet as="div">
            <div className="pg-filters-row">
              <div className="pg-field pg-field--grow">
                <label className="pg-label" htmlFor="sql-student-picker">Viewing student</label>
                {studentPicker}
              </div>

              {/* Every other "pick a student" page offers a way back to
                  the gate; handleClearSelection already existed here with
                  nothing calling it. */}
              <button type="button" className="pg-btn" onClick={handleClearSelection}>
                <X size={13} aria-hidden="true" />
                Clear
              </button>
            </div>
          </FilterSheet>
        )}

        {showList && quizHistories.length > 0 && (
          <div className="ov-tiles">
            {[
              { label: "Total quizzes", value: stats.total, Icon: BookOpen },
              { label: "Available", value: stats.available, Icon: Clock },
              { label: "Completed", value: stats.completed, Icon: CheckCircle },
              { label: "Passed", value: stats.passed, Icon: Award },
            ].map(({ label, value, Icon }) => (
              <div className="ov-tile" key={label}>
                <span className="ov-tile-label">
                  <Icon size={13} aria-hidden="true" />
                  {label}
                </span>
                <span className="ov-tile-value">{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* The gate above IS the unselected state now, so there is
            nothing to render here until a student is chosen. */}
        {!showList ? null : error ? (
          <div className="pg-state">
            <span className="pg-state-icon">
              <AlertCircle size={26} aria-hidden="true" />
            </span>
            <h3 className="pg-state-title">Unable to load quizzes</h3>
            <p className="pg-state-text">{error}</p>
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => fetchStudentQuizHistories(selectedStudent)}
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="td-list td-list--quiz" ref={listRef}>
            <NewCustomDataTable
              data={quizHistories.slice(0, shownCount)}
              headers={headers}
              loading={loading || cardsLoading}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              onRowClick={(row) => setOpenQuiz(row)}
              /* Every cell wraps its text in a span, so the default
                 ['TR','TD'] would mean clicking the text itself did
                 nothing. Nothing inside a row handles its own click. */
              validRowClickTagNames="*"
              rowProps={(row) => ({
                tabIndex: 0,
                role: "button",
                "aria-label": `Open ${row.quizName || "this quiz"}`,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenQuiz(row);
                  }
                },
              })}
            />

            {/* What the page buttons used to say, said once: how far down
                the list the reader is. */}
            <div className="pg-scroll-foot" ref={listEndRef}>
              {shownCount < quizHistories.length ? (
                <span className="pg-scroll-count">
                  {shownCount} of {quizHistories.length}
                </span>
              ) : (
                quizHistories.length > 0 && (
                  <span className="pg-scroll-count">
                    All {quizHistories.length} quiz
                    {quizHistories.length === 1 ? "" : "zes"} loaded
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {detail}
    </div>
  );
};

export default StudentQuizList;
